import { addMinutes } from 'date-fns'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState, type Dispatch, useCallback, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import { useForm, type FieldValues } from 'react-hook-form'

import type { PaymentMethodList, PostMessage } from '@/components/purchase/components/extendParkinglot/viewModel'
import { initList } from '@/components/purchase/components/parkinglot/model'

import collect from '@/utils/daangnEvents/purchase'
import airportEvent from '@/utils/gaEvents/airportEvent'

import type { AirportTicketDetail } from '@/api/server/airport/dto/airportDto'
import Shared from '@/shared'
import type { InputField } from '@/shared/components/FormInput'
import { convertToUtc, generateTimeOption } from '@/shared/utils/date'
import { phoneValidation } from '@/shared/utils/regexPatterns'

import { PARKINGLOT_TYPE } from '@/enums/detail'
import { CUSTOM_EXCEPTION_ERROR_CODE } from '@/enums/myTicket'
import { PLATFORM_TYPE } from '@/enums/purchase'

import { requestUserInput } from '@/api/client/myTicket'
import { requestVerifyCode, requestAuthGuest, requestAuthCode, requestParkinglotPayment } from '@/api/client/purchase'
import ticketApi from '@/api/client/ticket'
import { ClientRequestError } from '@/lib/axiosClient'

export interface AirportPurchaseViewModel {
  texts: object
  states: {
    step: number
    isPurchase: boolean
    allChecked: boolean
    isChecked: boolean
    checkedItems: { check: boolean; text: string; route: string }[]
    carNum: string
    phoneNum: string
    isSend: boolean
    authNum: string
    seconds: number
    isAuth: boolean | null
    errorText: string
    isToastOpen: boolean
    isErrorToast: boolean
    toastText: string
    isCancelModalOpen: boolean
    policyItems: { text: string; onClick: () => void }[]
    paymentList: PaymentMethodList
    isToggleOpen: boolean
    isCarNumChecked: boolean
    isNoCompensationOpen: boolean
    isCancelFeeOpen: boolean
    ticketData: AirportTicketDetail
    startDate: string
    endDate: string
    isDisabled: boolean
    frameUrl: string
    isOpen: boolean
    totalPrice: number
    isInComplete: boolean
    isComplete: boolean
    isNeedUserInfo: boolean
    isNeedConfirm: boolean
    useTime: string
    isFormNoticeOpen: boolean
    userInput: InputField[]
    register: ReturnType<typeof useForm>['register']
    errors: ReturnType<typeof useForm>['formState']['errors']
    isFormValid: boolean
    userInputs: FieldValues
    ticketSeq: number
  }
  setters: {
    setStep: (step: number) => void
    setIsPurchase: (isPurchase: boolean) => void
    setAllChecked: Dispatch<React.SetStateAction<boolean>>
    setCheckedItems: (checkedItems: { check: boolean; text: string; route: string }[]) => void
    setCarNum: Dispatch<React.SetStateAction<string>>
    setPhoneValue: Dispatch<React.SetStateAction<string>>
    setIsToastOpen: Dispatch<React.SetStateAction<boolean>>
    setIsCancelModalOpen: Dispatch<React.SetStateAction<boolean>>
    setIsErrorToast: Dispatch<React.SetStateAction<boolean>>
  }
  handlers: {
    onClickTermsBackBtn: () => void
    handleStep: (step: number) => void
    handleCheckChange: (event: React.ChangeEvent<HTMLInputElement>, index: number) => void
    handleAllCheckChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    handleCarNumChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handlePhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleAuthNumChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    expirationTime: () => string
    onClickPhoneNextBtn: () => Promise<void>
    requestAuthCodeAgain: () => Promise<void>
    handleSendAuthCode: () => Promise<void>
    onClickCancelModalCloseBtn: () => void
    onClickCancelModalConfirmBtn: () => void
    handleCloseBtn: () => void
    handlePolicyCheckChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    handleToggle: () => void
    radioHandler: (event: React.ChangeEvent<HTMLInputElement>) => void
    handleCarNumCheck: () => void
    confirmCarNumCheck: () => void
    handleNoCompensationClick: () => void
    handleCancelFeeInfoClick: () => void
    handlePaymentBtn: () => void
    handlePaymentModalClose: () => void
    handleRePayment: () => void
    handleShowMyTicketBtn: () => void
    handleNeedUserInfoBtn: () => void
    handleFormNoticeConfirmBtn: () => void
    handleFormNoticeOpen: () => void
    handleUserInfoCloseBtn: () => void
    handleNeedConfirmCloseBtn: () => void
    handleGetUserInputValue: (e: React.ChangeEvent<HTMLInputElement>) => void
  }
}

const useAirportPurchaseViewModel = ({ data }: { data: AirportTicketDetail }): AirportPurchaseViewModel => {
  const {
    register,
    getValues,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm()

  const { purchaseStep: step, setPurchaseStep: setStep } = Shared.States.usePurchaseStep()
  const { isPurchase, setIsPurchase } = Shared.States.useIsPurchase()
  const { guestSeq } = Shared.States.useStorageStore()
  const searchParams = useSearchParams()
  const beginTime = searchParams.get('sDate')
  const endTime = searchParams.get('eDate')
  const exitBeginTime = addMinutes(new Date(beginTime ?? ''), 30)
  const exitEndTime = addMinutes(new Date(endTime ?? ''), 30)
  const { id } = useParams<{ id: string }>()
  const router = useRouter()

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)

  const onClickCancelModalConfirmBtn = () => {
    setIsCancelModalOpen(false)
  }

  const onClickCancelModalCloseBtn = () => {
    setIsCancelModalOpen(true)
    setStep(0)
    setIsPurchase(false)
  }

  const handleCloseBtn = () => {
    setIsCancelModalOpen(true)
  }

  const handleStep = (step: number) => {
    setStep(step)
  }

  //Step 0: 이용약관 동의
  const [allChecked, setAllChecked] = useState(false)
  const [checkedItems, setCheckedItems] = useState([
    { check: false, text: '(필수) 모두의주차장 이용약관', route: process.env.NEXT_PUBLIC_PAGE_POLICY_HOST || '' },
    {
      check: false,
      text: '(필수) 개인정보 처리방침',
      route: `${process.env.NEXT_PUBLIC_PAGE_POLICY_HOST || ''}/privacy`
    },
    {
      check: false,
      text: '(필수) 위치정보 이용약관',
      route: `${process.env.NEXT_PUBLIC_PAGE_POLICY_HOST || ''}/location`
    }
  ])

  const onClickTermsBackBtn = () => {
    setStep(0)
    setIsPurchase(false)
  }

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newCheckedItems = [...checkedItems]

    newCheckedItems[index] = {
      ...newCheckedItems[index],
      check: event.target.checked
    }
    if (newCheckedItems.filter((el) => el.check === false)) {
      setAllChecked(false)
    }
    if (newCheckedItems.slice(0, 3).every((item) => item.check === true)) {
      setAllChecked(true)
    }
    setCheckedItems(newCheckedItems)
  }

  const handleAllCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAllChecked(event.target.checked)
    setCheckedItems((prevState) =>
      prevState.map((item) => ({
        ...item,
        check: event.target.checked
      }))
    )
  }

  //Step 1: 차량번호 입력
  const [carNum, setCarNum] = useState('')
  const [isCarNumChecked, setIsCarNumChecked] = useState(false)

  const handleCarNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCarNum(e.target.value)
  }

  const handleCarNumCheck = () => {
    setIsCarNumChecked(!isCarNumChecked)
  }

  const confirmCarNumCheck = () => {
    setStep(2)
    setIsCarNumChecked(false)
  }

  //Step 2: 휴대폰 인증
  const [phoneNum, setPhoneValue] = useState('')
  const [errorText, setErrorText] = useState('')
  const [authNum, setAuthNum] = useState('')
  const [toastText, setToastText] = useState('')
  const [token, setToken] = useState('')

  const [authCode, setAuthCode] = useState(0)
  const [seconds, setSeconds] = useState(180)

  const [isErrorToast, setIsErrorToast] = useState(false)
  const [isToastOpen, setIsToastOpen] = useState(false)
  const [isSend, setIsSend] = useState(false)
  const [isAuth, setIsAuth] = useState<boolean | null>(null)

  useEffect(() => {
    if (isSend) {
      if (seconds > 0) {
        const timerId = setInterval(() => {
          setSeconds(seconds - 1)
        }, 1000)
        return () => clearInterval(timerId)
      }
    }
  }, [seconds, isSend])

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneValue(e.target.value)
  }

  const handleAuthNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAuthNum(e.target.value)
  }

  const expirationTime = () => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
  }

  const onClickPhoneNextBtn = async () => {
    setToastText('')
    if (authCode && phoneValidation(phoneNum)) {
      try {
        const authPayload = {
          guestSeq: guestSeq
        }
        const verifyCodePayload = {
          authCodeSeq: authCode,
          code: authNum,
          phone: phoneNum
        }
        await requestVerifyCode(verifyCodePayload)
        const { data } = await requestAuthGuest(authPayload)
        setToken(data.accessToken)
        setIsAuth(true)
        setIsSend(false)
        setAuthNum('')
        setStep(3)
      } catch (err: unknown) {
        if (err instanceof ClientRequestError) {
          if (err.code !== 10000) {
            setErrorText(err.message)
            setIsAuth(false)
          } else {
            setIsErrorToast(true)
            setToastText(err.message)
          }
        } else {
          throw err
        }
      }
    }
  }

  const requestAuthCodeAgain = async () => {
    setSeconds(180)
    setAuthNum('')
    setToastText('')
    if (phoneNum) {
      setIsAuth(null)
      try {
        const payload = {
          phone: phoneNum
        }
        const { data } = await requestAuthCode(payload)
        setIsToastOpen(true)
        setAuthCode(data.authCodeSeq)
      } catch (err: unknown) {
        setIsSend(false)
        setIsAuth(false)
        if (err instanceof ClientRequestError) {
          setIsErrorToast(true)
          setToastText(err.message)
          setErrorText('')
        } else {
          throw err
        }
      }
    }
  }

  const handleSendAuthCode = async () => {
    setIsSend((prev) => !prev)
    try {
      const payload = {
        phone: phoneNum
      }
      const { data } = await requestAuthCode(payload)
      setAuthCode(data.authCodeSeq)
    } catch (err: unknown) {
      if (err instanceof ClientRequestError) {
        setIsErrorToast(true)
        setToastText(err.message)
      } else {
        throw err
      }
    }
  }

  // Step 3: 결제
  const policyItems = [
    { text: '주차대행 보상 불가 항목', onClick: () => handleNoCompensationClick() },
    { text: '주차대행 결제 취소수수료 안내', onClick: () => handleCancelFeeInfoClick() }
  ]

  const handleCancelFeeInfoClick = () => {
    setIsCancelFeeOpen(!isCancelFeeOpen)
  }

  const handleNoCompensationClick = () => {
    setIsNoCompensationOpen(!isNoCompensationOpen)
  }
  const [ticketData, setTicketData] = useState<AirportTicketDetail>(data)
  const [isNoCompensationOpen, setIsNoCompensationOpen] = useState(false)
  const [isCancelFeeOpen, setIsCancelFeeOpen] = useState(false)
  const [paymentList, setPaymentList] = useState<PaymentMethodList>(initList)
  const [isToggleOpen, setIsToggleOpen] = useState(true)
  const [isChecked, setIsChecked] = useState(false)
  const [frameUrl, setFrameUrl] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [isInComplete, setIsInComplete] = useState(false)
  const [isNeedUserInfo, setIsNeedUserInfo] = useState(false)
  const [isFormNoticeOpen, setIsFormNoticeOpen] = useState(false)
  const [isNeedConfirm, setIsNeedConfirm] = useState(false)
  const [ticketSeq, setTicketSeq] = useState(0)
  const [guestCode, setGuestCode] = useState('')
  const [userInput, setUserInput] = useState<InputField[]>([])
  const handlePolicyCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked)
  }
  const totalPrice =
    ticketData.basePrice + ticketData.addonPrice.map((el) => el.price).reduce((acc, cur) => acc + cur, 0)
  const startDate = generateTimeOption(beginTime ?? '')
  const endDate = generateTimeOption(endTime ?? '')
  const isSelected = paymentList.find((el) => el.checked === true) ?? false

  const userInputs: FieldValues = getValues()
  const watchedValues = watch()

  const isFormValid = useMemo(() => {
    return userInput.every((field) => {
      const value = watchedValues?.[field.title]
      return field.required ? value?.toString().trim() !== '' : true
    })
  }, [watchedValues, userInput])

  const useTime = `${startDate}/${endDate}`
  const isDisabled = isSelected && isChecked

  useEffect(() => {
    if (step === 3)
      airportEvent.renderAirportPayment(
        ticketData.parkinglotSeq,
        ticketData.parkinglotName,
        ticketData.couponSeq,
        ticketData.couponName
      )
  }, [step])

  useEffect(() => {
    if (isComplete) getUserInputData()
  }, [isComplete])

  const getUserInputData = async () => {
    try {
      const { data } = await ticketApi.getUserInputTemplate({
        couponSeq: id
      })
      if (!data.userInputTemplate) return
      const parsingData = JSON.parse(JSON.stringify(data.userInputTemplate))
      const rawData = JSON.parse(parsingData)
      const filteredData = rawData.filter((field: InputField) => field.title && field.title.trim() !== '')
      setUserInput(filteredData)
    } catch (err: unknown) {
      if (err instanceof ClientRequestError) {
        setIsErrorToast(true)
        setToastText(err.message)
      } else {
        throw err
      }
    }
  }

  const radioHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value
    const updatedRadioList = paymentList.map((radio) =>
      radio.value === selectedValue ? { ...radio, checked: true } : { ...radio, checked: false }
    )
    setPaymentList(updatedRadioList)
  }

  const handleToggle = () => {
    setIsToggleOpen((prev) => !prev)
  }

  const receiveMessage = useCallback((e: MessageEvent<PostMessage>) => {
    if (e.origin !== process.env.NEXT_PUBLIC_MODU_API_HOST) return
    if (e.data.result === 'success' && e.data.couSeq && e.data.guestCode) {
      setFrameUrl('')
      setIsComplete(true)
      setIsOpen(false)
      setTicketSeq(e.data.couSeq)
      setGuestCode(e.data.guestCode)
      collect()
    }
    if (e.data.result === 'failure') {
      setFrameUrl('')
      setIsChecked(false)
      setIsInComplete(true)
    }
  }, [])

  const handlePaymentModalClose = () => {
    setIsOpen(false)
  }

  const handleNeedUserInfoBtn = () => {
    setIsNeedUserInfo((prev) => !prev)
  }

  const handleUserInfoCloseBtn = () => {
    setIsNeedConfirm((prev) => !prev)
  }

  const handleNeedConfirmCloseBtn = () => {
    setIsPurchase(false)
    setStep(0)
    router.replace(
      `/my-ticket/${ticketSeq}?type=${PARKINGLOT_TYPE.PARKINGLOT.toLowerCase()}&guestCode=${guestCode}&token=${token}`
    )
  }

  const handleFormNoticeOpen = () => {
    setIsFormNoticeOpen((prev) => !prev)
  }

  const handleFormNoticeConfirmBtn = async () => {
    const formData = getValues()

    const userInputPathVar = {
      couSeq: String(ticketSeq)
    }
    const userInputPayload = {
      userInputJson: JSON.stringify(
        userInput.map((field) => ({
          title: field.title,
          placeholder: field.placeholder,
          type: field.type,
          required: field.required,
          value: formData[field.title]
        }))
      )
    }
    try {
      await requestUserInput(userInputPathVar, token, userInputPayload)
      airportEvent.clickUserInfoCompleteButton()
      setIsPurchase(false)
      setStep(0)
      router.replace(
        `/my-ticket/${ticketSeq}?type=${PARKINGLOT_TYPE.PARKINGLOT.toLowerCase()}&guestCode=${guestCode}&token=${token}`
      )
    } catch (err: unknown) {
      if (err instanceof ClientRequestError) {
        if (err.code === CUSTOM_EXCEPTION_ERROR_CODE.UNAVAILABLE_VALUE) {
          setIsErrorToast(true)
          setToastText('입력 내용이 2000자를 초과해 저장할 수 없습니다.')
        } else {
          setIsErrorToast(true)
          setToastText(err.message)
        }
      } else {
        throw err
      }
    }
  }

  const handlePaymentBtn = async () => {
    const payment = paymentList.find((el) => el.checked === true)
    if (payment && payment.id && beginTime && endTime) {
      const pathVar = {
        pgType: payment.id
      }
      const payload = {
        carNum: `${carNum}`,
        point: 0,
        price: totalPrice,
        totalPrice: totalPrice,
        couponSeq: data.couponSeq,
        predictBeginTime: convertToUtc(new Date(beginTime)),
        predictEndTime: convertToUtc(new Date(exitBeginTime)),
        predictExitBeginTime: convertToUtc(new Date(endTime)),
        predictExitEndTime: convertToUtc(new Date(exitEndTime)),
        authCodeSeq: authCode,
        platform: PLATFORM_TYPE.WEB
      }
      const auth = {
        token: token
      }
      try {
        const { data } = await requestParkinglotPayment(pathVar, payload, auth)
        const url = data.redirectUrl

        if (payment.id === 'naverpay') {
          router.replace(url)
        } else {
          if (!isMobile) {
            setIsOpen(true)
            setFrameUrl(url)
          }
          setFrameUrl(url)
        }
        window.addEventListener('message', receiveMessage, false)
      } catch (err: unknown) {
        if (err instanceof ClientRequestError) {
          setFrameUrl('')
          setIsErrorToast(true)
          setToastText(err.message)
        } else {
          throw err
        }
      }
    }
  }

  const handleRePayment = () => {
    setIsOpen(false)
    setIsInComplete(false)
    setFrameUrl('')
    setPaymentList([...initList])
  }

  const handleShowMyTicketBtn = () => {
    setIsPurchase(false)
    setStep(0)
    router.replace(
      `/my-ticket/${ticketSeq}?type=${PARKINGLOT_TYPE.PARKINGLOT.toLowerCase()}&guestCode=${guestCode}&token=${token}`
    )
  }

  const handleGetUserInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    setValue(name, value, { shouldDirty: true, shouldValidate: true })
  }

  return {
    texts: {},
    states: {
      step,
      isPurchase,
      allChecked,
      checkedItems,
      carNum,
      phoneNum,
      isSend,
      authNum,
      seconds,
      isAuth,
      errorText,
      isToastOpen,
      isErrorToast,
      toastText,
      isCancelModalOpen,
      policyItems,
      isChecked,
      paymentList,
      isToggleOpen,
      isCarNumChecked,
      isNoCompensationOpen,
      isCancelFeeOpen,
      ticketData,
      startDate,
      endDate,
      isDisabled,
      frameUrl,
      isOpen,
      totalPrice,
      isInComplete,
      isComplete,
      isNeedUserInfo,
      useTime,
      isFormNoticeOpen,
      isNeedConfirm,
      userInput,
      register,
      errors,
      isFormValid,
      userInputs,
      ticketSeq
    },
    setters: {
      setStep,
      setIsPurchase,
      setAllChecked,
      setCheckedItems,
      setCarNum,
      setPhoneValue,
      setIsToastOpen,
      setIsCancelModalOpen,
      setIsErrorToast
    },
    handlers: {
      onClickTermsBackBtn,
      handleStep,
      handleCheckChange,
      handleAllCheckChange,
      handleCarNumChange,
      handlePhoneChange,
      handleAuthNumChange,
      expirationTime,
      onClickPhoneNextBtn,
      requestAuthCodeAgain,
      handleSendAuthCode,
      onClickCancelModalCloseBtn,
      onClickCancelModalConfirmBtn,
      handleCloseBtn,
      handlePolicyCheckChange,
      handleToggle,
      radioHandler,
      handleCarNumCheck,
      confirmCarNumCheck,
      handleNoCompensationClick,
      handleCancelFeeInfoClick,
      handlePaymentBtn,
      handlePaymentModalClose,
      handleRePayment,
      handleShowMyTicketBtn,
      handleNeedUserInfoBtn,
      handleFormNoticeConfirmBtn,
      handleFormNoticeOpen,
      handleUserInfoCloseBtn,
      handleNeedConfirmCloseBtn,
      handleGetUserInputValue
    }
  }
}

export default useAirportPurchaseViewModel
